import React from 'react';
import { fmtCount, fmtFloat, fmtPct } from '../../metrics';
import {
  OdMetrics,
  PcClassMetrics,
  PogConfusionMatrix,
  prCorrectSureRate,
  prIncorrectSureRate,
  PrMetrics,
  prTop1Accuracy,
  RogAxisMetrics,
  RunResult,
} from '../../types';

interface MetricsSummaryProps {
  result: RunResult | null | undefined;
}

function MetricCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="eval-metric-card">
      <div className="eval-metric-label">{label}</div>
      <div className="eval-metric-value">{value}</div>
      {sub && <div className="eval-metric-sub">{sub}</div>}
    </div>
  );
}

function ClassBox({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 6,
        padding: '10px 12px',
        background: 'var(--surface-raised)',
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--text-secondary)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          marginBottom: 10,
        }}
      >
        {label}
      </div>
      <div className="eval-metrics-grid" style={{ margin: 0 }}>
        {children}
      </div>
    </div>
  );
}

function SectionHeader({ title }: { title: string }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
      {title}
    </div>
  );
}

// ── OD ────────────────────────────────────────────────────────────────────────

function OdClassBox({ od, label }: { od: OdMetrics | null | undefined; label: string }) {
  if (!od) return null;
  return (
    <ClassBox label={label}>
      <MetricCard label="Objects" value={fmtCount(od.total_objects)} sub="count" />
      <MetricCard label="Precision" value={fmtPct(od.precision)} sub="precision" />
      <MetricCard label="Recall" value={fmtPct(od.recall)} sub="recall" />
      <MetricCard label="F1" value={fmtPct(od.f1)} sub="f1" />
    </ClassBox>
  );
}

function OdSection({ od }: { od: RunResult['OD'] }) {
  if (!od) return null;
  return (
    <div style={{ gridColumn: '1 / -1' }}>
      <SectionHeader title="OD — Object Detection" />
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {([
          { key: 'products', label: 'Products' },
          { key: 'cartons', label: 'Cartons' },
          { key: 'shelves', label: 'Shelves' },
          { key: 'mods', label: 'Mods' },
          { key: 'template_boxes', label: 'Template Boxes' },
          { key: 'price_tags', label: 'Price Tags' },
        ] as const).map(({ key, label }) => (
          <div key={key} style={{ flex: '1 1 180px' }}>
            <OdClassBox od={od[key]} label={label} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── PR ────────────────────────────────────────────────────────────────────────

function PrClassBox({ pr, label }: { pr: PrMetrics | null | undefined; label: string }) {
  if (!pr) return null;
  const total = pr.total_objects;
  const top3Rate = total > 0 ? pr.correct_top3_cnt / total : null;
  const incUnsureRate = total > 0 ? pr.incorrect_top1_unsure_cnt / total : null;
  return (
    <ClassBox label={label}>
      <MetricCard label="Objects" value={fmtCount(total)} sub="count" />
      <MetricCard label="Correct Sure" value={fmtPct(prCorrectSureRate(pr))} sub="correct_sure / total" />
      <MetricCard label="Top1 Acc" value={fmtPct(prTop1Accuracy(pr))} sub="correct / total" />
      <MetricCard label="Top3 Acc" value={fmtPct(top3Rate)} sub="correct_top3 / total" />
      <MetricCard label="Incorr Sure" value={fmtPct(prIncorrectSureRate(pr))} sub="incorr_sure / total" />
      <MetricCard label="Incorr Unsure" value={fmtPct(incUnsureRate)} sub="incorr_unsure / total" />
      <MetricCard label="Avg Exp Conf" value={fmtFloat(pr.avg_exp_confidence)} sub="confidence" />
      <MetricCard label="Avg Top1 Conf" value={fmtFloat(pr.avg_top1_confidence)} sub="confidence" />
      <MetricCard label="Avg Top3 Conf" value={fmtFloat(pr.avg_top3_confidence)} sub="confidence" />
      <MetricCard label="Rec Sim Thr" value={fmtFloat(pr.recommended_similarity_thr)} sub="recommended" />
      <MetricCard label="Rec Delta Thr" value={fmtFloat(pr.recommended_delta_thr)} sub="recommended" />
    </ClassBox>
  );
}

function PrSection({ pr }: { pr: RunResult['PR'] }) {
  if (!pr) return null;
  return (
    <div style={{ gridColumn: '1 / -1' }}>
      <SectionHeader title="PR — Product Recognition" />
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 340px' }}><PrClassBox pr={pr.products} label="Products" /></div>
        <div style={{ flex: '1 1 340px' }}><PrClassBox pr={pr.cartons} label="Cartons" /></div>
      </div>
    </div>
  );
}

// ── PC ────────────────────────────────────────────────────────────────────────

function PcPogClassBox({ cls, label }: { cls: PcClassMetrics | null | undefined; label: string }) {
  if (!cls) return null;
  return (
    <ClassBox label={label}>
      <MetricCard label="Support" value={fmtCount(cls.support)} sub="GT items" />
      <MetricCard label="Precision" value={fmtPct(cls.precision)} sub="precision" />
      <MetricCard label="Recall" value={fmtPct(cls.recall)} sub="recall" />
      <MetricCard label="F1" value={fmtPct(cls.f1)} sub="f1" />
      <MetricCard label="Accuracy" value={fmtPct(cls.accuracy)} sub="accuracy" />
    </ClassBox>
  );
}

function ConfusionMatrixDisplay({ cm }: { cm: PogConfusionMatrix | null | undefined }) {
  if (!cm) return null;
  const { matrix, labels } = cm;
  const SHORT: Record<string, string> = { compliant: 'Compl', non_compliant: 'Non-C', out: 'Out', faceover: 'Face' };
  const short = (l: string) => SHORT[l] ?? l;
  const maxVal = Math.max(1, ...matrix.flat());
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 6, padding: '10px 12px', background: 'var(--surface-raised)', overflowX: 'auto' }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
        Confusion Matrix <span style={{ fontSize: 10, fontWeight: 400 }}>(rows=actual, cols=predicted)</span>
      </div>
      <table style={{ borderCollapse: 'collapse', fontSize: 11, fontFamily: 'var(--font-mono)' }}>
        <thead>
          <tr>
            <th style={{ padding: '2px 6px', color: 'var(--text-tertiary)', fontWeight: 400 }}></th>
            {labels.map((l) => (
              <th key={l} style={{ padding: '2px 8px', color: 'var(--text-secondary)', fontWeight: 600, textAlign: 'center' }}>{short(l)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {matrix.map((row, ri) => (
            <tr key={ri}>
              <td style={{ padding: '2px 6px', color: 'var(--text-secondary)', fontWeight: 600, whiteSpace: 'nowrap' }}>{short(labels[ri])}</td>
              {row.map((val, ci) => {
                const isDiag = ri === ci;
                const intensity = val / maxVal;
                const bg = isDiag
                  ? `rgba(59,130,246,${0.1 + intensity * 0.55})`
                  : val > 0 ? `rgba(239,68,68,${0.08 + intensity * 0.4})` : 'transparent';
                return (
                  <td key={ci} style={{ padding: '3px 8px', textAlign: 'center', background: bg, borderRadius: 3, color: val === 0 ? 'var(--text-tertiary)' : 'var(--text-primary)' }}>
                    {val}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RogAxisBox({ axis, label }: { axis: RogAxisMetrics | null | undefined; label: string }) {
  if (!axis) return null;
  return (
    <ClassBox label={label}>
      <MetricCard label="Support" value={fmtCount(axis.support)} sub="GT items" />
      <MetricCard label="TP" value={fmtCount(axis.tp)} sub="true pos" />
      <MetricCard label="FP" value={fmtCount(axis.fp)} sub="false pos" />
      <MetricCard label="FN" value={fmtCount(axis.fn)} sub="false neg" />
      <MetricCard label="Precision" value={fmtPct(axis.precision)} sub="precision" />
      <MetricCard label="Recall" value={fmtPct(axis.recall)} sub="recall" />
      <MetricCard label="F1" value={fmtPct(axis.f1)} sub="f1" />
    </ClassBox>
  );
}

function PcSection({ pc }: { pc: RunResult['PC'] }) {
  if (!pc) return null;
  return (
    <div style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: 16 }}>
      {pc.pog && (
        <div>
          <SectionHeader title="POG — Planogram Compliance" />
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 160px' }}><PcPogClassBox cls={pc.pog.compliant} label="Compliant" /></div>
            <div style={{ flex: '1 1 160px' }}><PcPogClassBox cls={pc.pog.non_compliant} label="Non-Compliant" /></div>
            <div style={{ flex: '1 1 160px' }}><PcPogClassBox cls={pc.pog.out} label="Out" /></div>
            <div style={{ flex: '1 1 160px' }}><PcPogClassBox cls={pc.pog.faceover} label="Faceover" /></div>
            <div style={{ flex: '2 1 280px' }}><ConfusionMatrixDisplay cm={pc.pog.confusion_matrix} /></div>
          </div>
        </div>
      )}
      {pc.rog && (
        <div>
          <SectionHeader title="ROG — Realogram Results" />
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 220px' }}><RogAxisBox axis={pc.rog.status_by_position} label="Status by Position" /></div>
            <div style={{ flex: '1 1 220px' }}><RogAxisBox axis={pc.rog.item_number_by_position} label="Item Number by Position" /></div>
            <div style={{ flex: '1 1 220px' }}><RogAxisBox axis={pc.rog.item_number_by_image} label="Item Number by Image" /></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export const MetricsSummary: React.FC<MetricsSummaryProps> = ({ result }) => {
  if (!result) return null;
  return (
    <div className="eval-metrics-grid">
      <OdSection od={result.OD} />
      <PrSection pr={result.PR} />
      <PcSection pc={result.PC} />
    </div>
  );
};
